import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../redux/hooks';
import { setCompanyAsUser } from '../redux/slices/authSlice';
import { fetchRestaurantsRequest } from '../redux/slices/restaurantSlice';
import { CompanySearchBar } from './company/CompanySearchBar';
import { CompanyTableHeader } from './company/CompanyTableHeader';
import { CompanyRow } from './company/CompanyRow';
import { CompanyEmptyState } from './company/CompanyEmptyState';
import { CompanyTableSkeleton } from './company/CompanyTableSkeleton';
import { EnvironmentToggle } from './common/EnvironmentToggle';
import { companyService } from '../services/companyService';
import { getFullName } from '../utils/dateFormatter';
import type { Company } from '../types/company';
import type { RootState } from '../redux/store';

export const CompanyList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { environment } = useSelector((state: RootState) => state.auth);
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch companies from API on mount and when environment changes
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await companyService.getUsers('snapmenu', 'superadmin');
        setCompanies(response.users);
      } catch (err) {
        console.error('Failed to fetch companies from API:', err);
        setError('Failed to load companies from server. Please try again.');
        // No fallback - companies stays as empty array
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [environment]); // Refetch when environment changes

  // Memoized filtering for performance
  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) {
      return companies;
    }

    const query = searchQuery.toLowerCase();
    return companies.filter(company => {
      const fullName = getFullName(company.first_name, company.last_name).toLowerCase();
      return (
        fullName.includes(query) ||
        company.email.toLowerCase().includes(query) ||
        company.address.toLowerCase().includes(query) ||
        company.company_name.toLowerCase().includes(query)
      );
    });
  }, [searchQuery, companies]);

  // Memoized click handler to avoid recreating function on every render
  const handleCompanyClick = useCallback((company: Company) => {
    // Set account ID based on current environment
    // Only one ID is set at a time - the other is null
    const accountId = environment === 'sandbox' ? company.account_id : null;
    const prodAccountId = environment === 'production' ? company.account_id : null;

    // Set company as authenticated user in Redux (completely replace user data with company data)
    dispatch(setCompanyAsUser({
      accountId,           // Set if sandbox, null if production
      prodAccountId,       // Set if production, null if sandbox
      email: company.email,
      firstName: company.first_name,
      lastName: company.last_name,
      companyName: company.company_name,
      phone: company.phone,
      address: company.address,
      businessType: company.business_type,
      selectedFeatures: company.selected_features,
      isActivated: company.is_activated,
      isoLicense: company.iso_license,
      numberOfMerchants: company.number_of_merchants,
      comments: company.comments,
      isApproved: company.is_approved,
      approvedBy: company.approved_by,
    }));
    
    // Fetch restaurants for the selected company
    dispatch(fetchRestaurantsRequest());
    
    // Navigate immediately (don't wait for restaurants to load)
    navigate('/api-reference/introduction', { replace: true });
  }, [dispatch, environment, navigate]);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <main className="max-w-screen-2xl mx-auto px-8 py-4">
        <div className="space-y-4">
          {/* Page Title - Enhanced UX */}
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#EF4421] flex items-center justify-center shadow-lg flex-shrink-0">
              <svg 
                className="w-6 h-6 text-white" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" 
                />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[var(--text-primary)]">
                Companies
              </h1>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                Select a company to view analytics and details
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-yellow-700">{error}</p>
              </div>
            </div>
          )}

          {/* Search Bar and Environment Toggle */}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between w-full">
            <div className="w-full md:w-2/3 lg:w-1/2 max-w-xl">
              <CompanySearchBar 
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>
            <div className="w-full md:w-auto">
              <EnvironmentToggle skipAccessControl={true} />
            </div>
          </div>

          {/* Company Table */}
          <div 
            role="table" 
            aria-label="Company list"
            className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden max-h-[70vh] md:max-h-[75vh] flex flex-col"
          >
            {/* Table Header - Sticky */}
            <CompanyTableHeader />

            {/* Table Body */}
            <div className="divide-y divide-[var(--border-color)] overflow-y-auto flex-1">
              {loading ? (
                <CompanyTableSkeleton />
              ) : filteredCompanies.length > 0 ? (
                filteredCompanies.map((company) => (
                  <CompanyRow
                    key={company.account_id}
                    company={company}
                    onClick={handleCompanyClick}
                  />
                ))
              ) : (
                <CompanyEmptyState searchQuery={searchQuery} />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CompanyList;
